import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  Chip, Alert, LinearProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { CloudUpload, ArrowBack, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const ImportPptx = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [kcOverride, setKcOverride] = useState('');
  const [kcs, setKcs] = useState([]);
  const [importResult, setImportResult] = useState(null);

  const loadKcs = async () => {
    try {
      const response = await api.get('/kcs');
      setKcs(response.data);
    } catch (error) {
      console.error('KCs laden fehlgeschlagen');
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.name.endsWith('.pptx')) {
      setSelectedFile(file);
      setPreview(null);
      setImportResult(null);
    } else {
      toast.error('Bitte eine .pptx Datei auswählen');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handlePreview = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/import/pptx-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreview(response.data);
      setSelectedTickets(response.data.tickets.map((_, i) => i));
      if (response.data.competence_center_id) {
        setKcOverride(response.data.competence_center_id);
      }
      await loadKcs();
    } catch (error) {
      toast.error(error.response?.data?.error || 'PPTX konnte nicht gelesen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview || selectedTickets.length === 0) return;

    setImporting(true);
    try {
      const ticketsToImport = selectedTickets.map(i => preview.tickets[i]);
      const response = await api.post('/import/pptx', {
        tickets: ticketsToImport,
        competence_center_id: kcOverride || null,
        filePath: preview.filePath,
        originalName: preview.originalName
      });

      setImportResult(response.data);
      toast.success(`${response.data.created} Ticket(s) erfolgreich importiert`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const toggleTicket = (index) => {
    setSelectedTickets(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const toggleAll = () => {
    if (selectedTickets.length === preview?.tickets?.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(preview.tickets.map((_, i) => i));
    }
  };

  return (
    <Container maxWidth="lg">
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/tickets')} sx={{ mb: 2 }}>
        Zurück zu Tickets
      </Button>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        PPTX Import
      </Typography>

      {/* Upload Zone */}
      {!importResult && (
        <Paper
          sx={{
            p: 4, mb: 3, textAlign: 'center',
            border: '2px dashed #ccc', borderRadius: '16px',
            cursor: 'pointer',
            '&:hover': { borderColor: '#CC0000', backgroundColor: 'rgba(204,0,0,0.02)' }
          }}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pptx"
            hidden
            onChange={(e) => handleFileSelect(e.target.files[0])}
          />
          <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 1 }} />
          <Typography variant="h6" color="textSecondary">
            PPTX-Datei hierher ziehen oder klicken
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Bayernwerk Entstörungs-Präsentationen (.pptx)
          </Typography>
          {selectedFile && (
            <Chip label={selectedFile.name} color="primary" sx={{ mt: 2 }} onDelete={() => setSelectedFile(null)} />
          )}
        </Paper>
      )}

      {selectedFile && !preview && !importResult && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button variant="contained" onClick={handlePreview} disabled={loading} size="large"
            sx={{ backgroundColor: '#CC0000', '&:hover': { backgroundColor: '#8B0000' } }}>
            {loading ? 'Wird analysiert...' : 'Vorschau laden'}
          </Button>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Preview */}
      {preview && !importResult && (
        <>
          <Paper sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Gemeinde: {preview.gemeinde}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {preview.tickets.length} Ticket(s) erkannt
                  {preview.competence_center_name && ` — KC: ${preview.competence_center_name}`}
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 250 }} size="small">
                <InputLabel>Kompetenz-Center</InputLabel>
                <Select
                  value={kcOverride}
                  onChange={(e) => setKcOverride(e.target.value)}
                  label="Kompetenz-Center"
                >
                  <MenuItem value="">Keines</MenuItem>
                  {kcs.map(kc => (
                    <MenuItem key={kc.id} value={kc.id}>{kc.code} — {kc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTickets.length === preview.tickets.length}
                        indeterminate={selectedTickets.length > 0 && selectedTickets.length < preview.tickets.length}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Slide</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Titel</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ortsteil</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>KVS/ONS</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Seriennummer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.tickets.map((ticket, index) => (
                    <TableRow key={index} selected={selectedTickets.includes(index)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedTickets.includes(index)} onChange={() => toggleTicket(index)} />
                      </TableCell>
                      <TableCell>{ticket.slideNumber}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{ticket.title}</TableCell>
                      <TableCell>{ticket.district || '—'}</TableCell>
                      <TableCell>
                        {ticket.kvs_designation && <Chip label={ticket.kvs_designation} size="small" color="info" sx={{ mr: 0.5 }} />}
                        {ticket.ons_designation && <Chip label={ticket.ons_designation} size="small" color="warning" />}
                        {!ticket.kvs_designation && !ticket.ons_designation && '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {ticket.serial_number || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.description || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="outlined" onClick={() => { setPreview(null); setSelectedFile(null); }}>
              Abbrechen
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || selectedTickets.length === 0}
              sx={{ backgroundColor: '#CC0000', '&:hover': { backgroundColor: '#8B0000' } }}
            >
              {importing ? 'Importiere...' : `${selectedTickets.length} Ticket(s) importieren`}
            </Button>
          </Box>
        </>
      )}

      {/* Result */}
      {importResult && (
        <Paper sx={{ p: 3, borderRadius: '16px' }}>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Import abgeschlossen
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              {importResult.message}
            </Typography>
            {importResult.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                {importResult.errors.map((e, i) => (
                  <div key={i}>{e.title}: {e.error}</div>
                ))}
              </Alert>
            )}
            <Button variant="contained" onClick={() => navigate('/tickets')}
              sx={{ backgroundColor: '#CC0000', '&:hover': { backgroundColor: '#8B0000' } }}>
              Zur Ticket-Übersicht
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default ImportPptx;
